import i18n from '@/i18n';
import { attributeValueDisplayLabel } from '@/tiktok-platform/utils/tiktokAttributeTemplate';

function pkgText(key, fallback, options) {
  if (!i18n.isInitialized) return fallback;
  return i18n.t(key, { ns: 'workbench', defaultValue: fallback, ...(options || {}) }) || fallback;
}

/**
 * 从 SKU 行回填商品级包裹字段（旧草稿只写在尺码行时用）。
 * @param {Array} skcList
 * @param {'weight'|'length'|'width'|'height'} key
 * @returns {string}
 */
export function firstFilledSkuPackageField(skcList, key) {
  for (const skc of Array.isArray(skcList) ? skcList : []) {
    for (const sku of Array.isArray(skc?.skuRows) ? skc.skuRows : []) {
      const v = String(sku?.[key] ?? '').trim();
      if (v !== '' && Number(v) > 0) return v;
    }
  }
  return '';
}

const PATCH_FIELDS = [
  'weight',
  'netWeight',
  'length',
  'width',
  'height',
  'quantity',
  'quantityUnit',
  'numberOfPieces',
  'pieceUnitCode',
  'totalContains',
  'skuClassification',
  'individuallyPacked',
  'packageType',
];

export function skuPackageSizeKey(sku) {
  if (!sku) return '';
  if (sku.sizeIsCustom || sku.sizeExtraValue) {
    const raw = String(sku.sizeExtraValue || sku.sizeLabel || '').trim();
    return raw ? `custom:${raw}` : '';
  }
  if (sku.sizeValueId != null && String(sku.sizeValueId).trim() !== '') {
    return `id:${String(sku.sizeValueId)}`;
  }
  const label = String(sku.sizeLabel || '').trim();
  return label ? `label:${label}` : '';
}

export function skuPackageSizeLabel(sku, sizeAttr) {
  if (sku?.sizeIsCustom || sku?.sizeExtraValue) {
    return attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue)
      || String(sku.sizeExtraValue || sku.sizeLabel || '').trim()
      || '自定义';
  }
  return attributeValueDisplayLabel(
    sizeAttr?.values?.find((v) => String(v.id) === String(sku?.sizeValueId)) || sku?.sizeLabel,
  ) || String(sku?.sizeLabel || sku?.sizeValueId || '').trim() || '—';
}

export function seedPackageBatchFromSku(sku) {
  if (!sku || typeof sku !== 'object') return {};
  return {
    weight: String(sku.weight ?? '').trim(),
    netWeight: String(sku.netWeight ?? '').trim(),
    length: String(sku.length ?? '').trim(),
    width: String(sku.width ?? '').trim(),
    height: String(sku.height ?? '').trim(),
    quantity: String(sku.numberOfPieces ?? sku.quantity ?? '').trim(),
    quantityUnit: String(sku.pieceUnitCode || sku.quantityUnit || '1'),
    numberOfPieces: String(sku.numberOfPieces ?? sku.quantity ?? '').trim(),
    pieceUnitCode: String(sku.pieceUnitCode || sku.quantityUnit || '1'),
    totalContains: String(sku.totalContains ?? '').trim(),
    skuClassification: String(sku.skuClassification || '1'),
    individuallyPacked: sku.individuallyPacked === 0 || sku.individuallyPacked === '0'
      ? '0'
      : (sku.individuallyPacked === 1 || sku.individuallyPacked === '1' ? '1' : ''),
    packageType: sku.packageType === 0 || sku.packageType === '0' ? '' : String(sku.packageType ?? ''),
    isSensitive: Number(sku.isSensitive || 0) === 1 ? '1' : '0',
    sensitiveList: Array.isArray(sku.sensitiveList) ? [...sku.sensitiveList] : [],
    accessories: Array.isArray(sku.accessories) && sku.accessories.length
      ? sku.accessories.map((row) => ({ ...row }))
      : [{ vid: '', num: '', unitCode: 1, unitName: '件', thumbUrl: '', label: '' }],
  };
}

/** @deprecated 使用 seedPackageBatchFromSku */
function seedFromSku(sku) {
  return seedPackageBatchFromSku(sku);
}

/** 从当前 SKU 行收集去重尺码，并带上该尺码已有包装值（取首次出现）。 */
export function collectUniquePackageSizes(skcList, sizeAttr) {
  const seen = new Map();
  (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
    (skc?.skuRows || []).forEach((sku) => {
      const key = skuPackageSizeKey(sku);
      if (!key || seen.has(key)) return;
      seen.set(key, {
        key,
        label: skuPackageSizeLabel(sku, sizeAttr),
        seed: seedFromSku(sku),
      });
    });
  });
  return [...seen.values()];
}

export function packagePatchHasValue(patch) {
  if (!patch || typeof patch !== 'object') return false;
  if (Object.prototype.hasOwnProperty.call(patch, 'isSensitive')) return true;
  if (Array.isArray(patch.accessories)) {
    return patch.accessories.some((row) => Number(row?.vid) > 0 || String(row?.num ?? '').trim() !== '');
  }
  if (Array.isArray(patch.sensitiveList) && patch.sensitiveList.length) return true;
  return [
    'weight',
    'netWeight',
    'length',
    'width',
    'height',
    'quantity',
    'numberOfPieces',
    'totalContains',
    'skuClassification',
    'individuallyPacked',
    'packageType',
  ].some((field) => String(patch[field] ?? '').trim() !== '');
}

export function packageBatchHasAnyValue(bySize) {
  return Object.values(bySize || {}).some((patch) => packagePatchHasValue(patch));
}

/** 正数校验：空串视为未填；非法返回 null */
export function parsePositiveNumberInput(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return { ok: true, empty: true, value: '' };
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) {
    return { ok: false, empty: false, value: raw, message: pkgText('publishPkg.positiveNum', '请输入大于 0 的数字') };
  }
  return { ok: true, empty: false, value: raw, number: n };
}

/**
 * 包装尺寸：Create Product package_dimensions 要求长/宽/高为正数（正整数）。
 * 不再要求 Temu「最长 ≥ 次长 ≥ 最短」。
 */
export function validateDimsOrder(length, width, height) {
  const len = parsePositiveNumberInput(length);
  const wid = parsePositiveNumberInput(width);
  const hei = parsePositiveNumberInput(height);
  if (!len.ok) return len.message || pkgText('publishPkg.lenInvalid', '长度无效');
  if (!wid.ok) return wid.message || pkgText('publishPkg.widInvalid', '宽度无效');
  if (!hei.ok) return hei.message || pkgText('publishPkg.heiInvalid', '高度无效');
  if (len.empty && wid.empty && hei.empty) return pkgText('publishPkg.dimOne', '请至少填写一项尺寸');
  if (len.empty || wid.empty || hei.empty) return pkgText('publishPkg.dimAll', '请完整填写长、宽、高');
  if (!Number.isInteger(len.number) || !Number.isInteger(wid.number) || !Number.isInteger(hei.number)) {
    return pkgText('publishPkg.dimInt', '长 / 宽 / 高须为正整数（单位 cm）');
  }
  return '';
}

function applyPatchToSku(sku, patch) {
  if (!packagePatchHasValue(patch)) return sku;
  const next = { ...sku };
  PATCH_FIELDS.forEach((field) => {
    if (patch[field] == null) return;
    const val = String(patch[field]).trim();
    if (field === 'individuallyPacked') {
      if (val === '' || val === '0' || val === '1') next[field] = val;
      return;
    }
    if (!val) return;
    next[field] = val;
  });
  if (Object.prototype.hasOwnProperty.call(patch, 'isSensitive')) {
    next.isSensitive = Number(patch.isSensitive) === 1 ? 1 : 0;
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'sensitiveList')) {
    next.sensitiveList = Array.isArray(patch.sensitiveList) ? [...patch.sensitiveList] : [];
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'accessories') && Array.isArray(patch.accessories)) {
    next.accessories = patch.accessories.map((row) => ({ ...row }));
  }
  // 与 SKU 分类字段对齐
  if (next.quantity != null && String(next.quantity).trim() !== '') {
    next.numberOfPieces = String(next.quantity);
  }
  if (next.numberOfPieces != null && String(next.numberOfPieces).trim() !== '') {
    next.quantity = String(next.numberOfPieces);
  }
  if (next.quantityUnit != null && String(next.quantityUnit).trim() !== '') {
    next.pieceUnitCode = String(next.quantityUnit);
  }
  if (next.pieceUnitCode != null && String(next.pieceUnitCode).trim() !== '') {
    next.quantityUnit = String(next.pieceUnitCode);
  }
  const cls = String(next.skuClassification || '1');
  if (cls === '1') {
    next.individuallyPacked = '';
  }
  if (Number(next.isSensitive) !== 1) {
    next.sensitiveList = [];
  }
  return next;
}

/** 按尺码把包装字段写回所有颜色的对应 SKU。未填的尺码保持原值。 */
export function applyPackageBatchBySize(skcList, bySize) {
  const map = bySize && typeof bySize === 'object' ? bySize : {};
  return (Array.isArray(skcList) ? skcList : []).map((skc) => ({
    ...skc,
    skuRows: (skc?.skuRows || []).map((sku) => {
      const key = skuPackageSizeKey(sku);
      return applyPatchToSku(sku, key ? map[key] : null);
    }),
  }));
}

/** 同一套值写到全部 SKU。 */
export function applyPackageBatchToAllSkus(skcList, patch) {
  if (!packagePatchHasValue(patch)) {
    return Array.isArray(skcList) ? skcList : [];
  }
  return (Array.isArray(skcList) ? skcList : []).map((skc) => ({
    ...skc,
    skuRows: (skc?.skuRows || []).map((sku) => applyPatchToSku(sku, patch)),
  }));
}
